"use client";

import React, { useMemo } from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import AutoScroll from "embla-carousel-auto-scroll";

import DecorationLine from "@/components/global/ui/DecorationLine";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";

import { PageCareerSchema } from "@/sanity/pages/PageCareerTemplate";

export default function GallerySection({
  data,
  className,
}: {
  data: PageCareerSchema;
  className?: string;
}) {
  const images = data?.galleryImages?.filter((image) => image?.src);

  const autoScroll = useMemo(
    () =>
      AutoScroll({
        speed: 1,
        startDelay: 0,
        stopOnInteraction: false,
      }),
    [],
  );

  if (!images?.length) return null;

  return (
    <section
      className={cn("relative overflow-clip bg-[#F3F2F1] py-[2rem]", className)}>
      <DecorationLine
        className="top-0"
        orientation="horizontal"
        style="dark"
      />

      {/*
        The strip is wider than the viewport by design, so it scrolls itself.
        The list is doubled because a seamless loop needs more slide width than
        the viewport — with only a handful of photos embla would otherwise snap
        back visibly at the end of the run.
      */}
      <Carousel
        opts={{ align: "start", loop: true }}
        plugins={[autoScroll]}>
        <CarouselContent className="-ml-[0.64rem] sm:-ml-[1rem]">
          {[...images, ...images].map((image, index) => (
            <CarouselItem
              key={index}
              className="basis-auto pl-[0.64rem] sm:pl-[1rem]">
              {/* the strip is the desktop one at 0.64 scale on mobile, tile and gutter alike */}
              <div className="relative h-[38.4rem] w-[30.72rem] sm:h-[60rem] sm:w-[48rem]">
                <Image
                  className="absolute left-0 top-0 !h-full !w-full select-none object-cover"
                  src={image}
                  alt=""
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <DecorationLine
        className="bottom-0"
        orientation="horizontal"
        style="dark"
      />
    </section>
  );
}
